import { memo, type ReactNode } from 'react'

import { cn } from '@/lib/utils'

import type { DataTableToolbarProps } from './data-table.interface'
import { DataTableBulkActions } from './data-table-bulk-actions.atom'
import { DataTableToolbarSearchFilters } from './data-table-toolbar-search-filters.atom'
import { useDataTableFilters } from './useDataTableFilters.hook'

/**
 * Renders the data table toolbar with search, filters, bulk actions, and custom actions.
 */
const DataTableToolbarBase: React.FC<DataTableToolbarProps> = ({
  searchValue,
  onSearchChange,
  searchPlaceholder,
  filterList,
  filterValueList = [],
  onFilterChange,
  selectedCount = 0,
  bulkActionList = [],
  onBulkAction,
  toolbarActions,
  className,
}): ReactNode => {
  const { hasActiveFilters, handleFilterChange, handleClearFilters, getFilterValues } =
    useDataTableFilters({ filterValueList, onFilterChange })

  const showBulkActions = selectedCount > 0 && bulkActionList.length > 0
  const showFilters = !!(filterList && filterList.length > 0 && onFilterChange)

  const leftContent = showBulkActions ? (
    <DataTableBulkActions
      selectedCount={selectedCount}
      bulkActionList={bulkActionList}
      onBulkAction={onBulkAction}
    />
  ) : (
    <DataTableToolbarSearchFilters
      searchValue={searchValue}
      onSearchChange={onSearchChange}
      searchPlaceholder={searchPlaceholder}
      filterList={filterList}
      showFilters={showFilters}
      hasActiveFilters={hasActiveFilters}
      getFilterValues={getFilterValues}
      onFilterChange={handleFilterChange}
      onClearFilters={handleClearFilters}
    />
  )

  return (
    <div className={cn('data-table-toolbar flex items-center justify-between', className)}>
      <div className="data-table-toolbar-left flex flex-1 items-center gap-2">{leftContent}</div>
      {toolbarActions && (
        <div className="data-table-toolbar-right flex items-center gap-2">{toolbarActions}</div>
      )}
    </div>
  )
}

export const DataTableToolbar = memo(DataTableToolbarBase)

DataTableToolbar.displayName = 'DataTableToolbar'
